import type { Href } from 'expo-router';

import type { LocaleLinks } from '@todo/core/i18n/locale-links-context';
import { SUPPORTED_LOCALES, isSupportedLocale, type Locale } from '@todo/core/i18n/locales';
import { sanityClient } from '@todo/core/sanity/client';
import { allArticlesDetailQuery, type ArticleDetail } from '@todo/core/sanity/queries';
import { setPrefetched } from '@todo/core/static-prefetch';

// Shared by the native and web variants of app/[lang]/articles/[slug].
// Articles genuinely differ per locale (separate Sanity documents), so unlike
// events/tours this fetches fresh per {lang} rather than sharing one cached
// promise across all three.
// NOTE the argument shape. expo-router calls this as
// `generateStaticParams({ params: parentParams })` — one object with a `params`
// key, not the params themselves. Declaring it as `(params: { lang })` made
// `params.lang` undefined, so `isSupportedLocale` fell through to 'en' and this
// query returned the ENGLISH slugs for all three locales: /fr/articles/… and
// /ru/articles/… were emitted at English slugs, found no matching document at
// render time, and shipped as noindex stubs — while the real French and Russian
// articles (which have their own localized slugs) got no URL at all.
export async function generateArticleStaticParams({
  params,
}: {
  params: { lang?: string };
}): Promise<{ lang: string; slug: string }[]> {
  const locale: Locale = isSupportedLocale(params.lang) ? params.lang : 'en';
  const articles = await sanityClient.fetch<ArticleDetail[]>(allArticlesDetailQuery, {
    lang: locale,
  });
  const withSlug = (articles ?? []).filter((a) => typeof a.slug === 'string' && a.slug);
  for (const article of withSlug) setPrefetched('article', `${locale}:${article.slug}`, article);
  return withSlug.map((article) => ({ lang: locale, slug: article.slug }));
}

// Per-locale destinations for the language switcher on an article page: the
// sibling's own localized slug when a translation exists (from the
// document's translation.metadata group), otherwise the section's listing
// page. Never guesses the current slug under another locale — that's the
// export bug this file's comment above documents; see
// docs/article-i18n-hreflang-findings.md.
export function buildArticleLocaleLinks(
  article: ArticleDetail | null,
  locale: Locale,
): LocaleLinks | null {
  if (!article) return null;

  const links: LocaleLinks = {
    [locale]: { pathname: '/[lang]/articles/[slug]', params: { lang: locale, slug: article.slug } } as Href,
  };
  for (const t of article.translations ?? []) {
    if (isSupportedLocale(t.locale) && t.locale !== locale && t.slug) {
      links[t.locale] = {
        pathname: '/[lang]/articles/[slug]',
        params: { lang: t.locale, slug: t.slug },
      } as Href;
    }
  }
  for (const l of SUPPORTED_LOCALES) {
    if (!links[l]) links[l] = { pathname: '/[lang]/articles', params: { lang: l } } as Href;
  }
  return links;
}
